import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  CreatePlantillaPositionRequest,
  PlantillaItemListItem,
  PlantillaPositionListItem,
  PlantillaPositionOption,
  SalaryGradeListItem,
  SalaryGradeOption,
  SalaryGradeRequest,
  UpdatePlantillaPositionRequest,
} from './plantillaTypes';

/** The staffing pattern's list query adds the unit and vacancy facets. */
interface PlantillaQuery extends ListQuery {
  /** Narrows to the posts drawn from one catalog title. A scope, not a facet. */
  positionId?: string;
  /** Comma-separated org-unit ids. */
  unit?: string;
  /** Comma-separated: `vacant`, `filled`. */
  vacancy?: string;
}

/** The salary schedule's list query adds the salary-type facet. */
interface SalaryGradeQuery extends ListQuery {
  /** Comma-separated: `general`, `health`. */
  type?: string;
}

function toQueryString(query: PlantillaQuery & SalaryGradeQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  /*
    Facets are sent whenever the key is present, empty string included — an empty value is the
    reader saying "none of these", which matches no row. A truthiness check here would drop it
    and the server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.unit !== undefined) params.set('unit', query.unit);
  if (query.vacancy !== undefined) params.set('vacancy', query.vacancy);
  // A scope rather than a facet, so it is sent only when set — never as an empty value.
  if (query.positionId) params.set('positionId', query.positionId);
  if (query.type !== undefined) params.set('type', query.type);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const plantillaApi = {
  /** Soft-deletes every position in the module at once; resolves with how many were deleted. */
  clear: () => api<number>('/api/plantilla/clear', { method: 'POST' }),

  list: (query: PlantillaQuery, signal?: AbortSignal) =>
    api<PagedResult<PlantillaPositionListItem>>(`/api/plantilla${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<PlantillaPositionListItem>(`/api/plantilla/${id}`, { signal }),

  options: (signal?: AbortSignal) =>
    api<PlantillaPositionOption[]>('/api/plantilla/options', { signal }),

  create: (request: CreatePlantillaPositionRequest) =>
    api<PlantillaPositionListItem>('/api/plantilla', { method: 'POST', body: request }),

  update: (id: string, request: UpdatePlantillaPositionRequest) =>
    api<PlantillaPositionListItem>(`/api/plantilla/${id}`, { method: 'PUT', body: request }),

  /** Soft delete. Refused while a slot is filled; takes the position's slots with it. */
  deactivate: (id: string) => api<void>(`/api/plantilla/${id}`, { method: 'DELETE' }),

  /** The position comes back with no re-authorized headcount — slots are restored one by one. */
  activate: (id: string) => api<void>(`/api/plantilla/${id}/restore`, { method: 'POST' }),

  // ── Slots ──────────────────────────────────────────────────────────────────────────

  slots: (positionId: string, includeDeleted = false, signal?: AbortSignal) =>
    api<PlantillaItemListItem[]>(
      `/api/plantilla/${positionId}/slots${includeDeleted ? '?includeDeleted=true' : ''}`,
      { signal },
    ),

  /**
   * **It answers with the post's WHOLE slot list, not the rows it just created.** A caller that
   * wants the new one takes the highest `sequenceNumber` — the server appends from the highest
   * it has ever issued, so the newest rows are the last ones numbered. Reading `[0]` picks the
   * post's FIRST slot, which is what a caller did until 09 Oct 2026.
   */
  addSlots: (positionId: string, count: number) =>
    api<PlantillaItemListItem[]>(`/api/plantilla/${positionId}/slots`, {
      method: 'POST',
      body: { count },
    }),

  removeSlot: (itemId: string) => api<void>(`/api/plantilla/slots/${itemId}`, { method: 'DELETE' }),

  restoreSlot: (itemId: string) =>
    api<void>(`/api/plantilla/slots/${itemId}/restore`, { method: 'POST' }),
};

export const salaryGradesApi = {
  clear: () => api<number>('/api/salary-grades/clear', { method: 'POST' }),

  list: (query: SalaryGradeQuery, signal?: AbortSignal) =>
    api<PagedResult<SalaryGradeListItem>>(`/api/salary-grades${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<SalaryGradeListItem>(`/api/salary-grades/${id}`, { signal }),

  /** The distinct grade/step pairs a position can be pegged to. Unpaged. */
  options: (signal?: AbortSignal) =>
    api<SalaryGradeOption[]>('/api/salary-grades/options', { signal }),

  create: (request: SalaryGradeRequest) =>
    api<SalaryGradeListItem>('/api/salary-grades', { method: 'POST', body: request }),

  update: (id: string, request: SalaryGradeRequest) =>
    api<SalaryGradeListItem>(`/api/salary-grades/${id}`, { method: 'PUT', body: request }),

  deactivate: (id: string) => api<void>(`/api/salary-grades/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/salary-grades/${id}/restore`, { method: 'POST' }),
};
